import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  NgMultiLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import { CalendarService } from '../../services/calendar.service';

/**
 * "View by Installation Ability" multiselect for the Calendar Settings
 * side modal.
 *
 * Source list comes from `CalendarService.getAvailableAbilities()` — a
 * broker subject that is populated by whichever page is active:
 *   - `/calendar` pushes via `CrewScheduleStore.mapToCrewGroups`
 *   - `/monthly-capacity` pushes via `MonthlyCapacityComponent.mapToCapacityCrewGroups`
 *
 * Defaults to an empty selection (no narrowing). Hydrates from
 * `CalendarService.getCurrentSelectedAbilities()` so the pane reflects
 * whatever was last applied.
 */
@Component({
  selector: 'app-installation-ability-selector',
  imports: [
    NgSelectComponent,
    ReactiveFormsModule,
    NgOptionTemplateDirective,
    NgMultiLabelTemplateDirective,
  ],
  templateUrl: './installation-ability-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallationAbilitySelectorComponent implements OnInit {
  private readonly calendarService = inject(CalendarService);

  @Output() abilitiesSelected = new EventEmitter<string[]>();

  abilities: readonly string[] = [];
  readonly formControl = new FormControl<string[]>([], { nonNullable: true });

  constructor() {
    this.formControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((selected) => {
        this.abilitiesSelected.emit(selected ?? []);
      });

    this.calendarService.getAvailableAbilities()
      .pipe(takeUntilDestroyed())
      .subscribe((available) => {
        this.abilities = available;
      });
  }

  ngOnInit(): void {
    const applied = this.calendarService.getCurrentSelectedAbilities();
    if (applied.length > 0) {
      this.formControl.patchValue([...applied], { emitEvent: false });
    }
  }

  resetSelection(): void {
    this.formControl.patchValue([], { emitEvent: false });
  }
}
